import React, { useState } from 'react';
import { Modal, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import styled from 'styled-components';
import FontAwesomeIcon from '../../common/FontAwesomeIcon';

const getCommentChipConfig = (commentType) => {
    if (commentType === 'correction') {
        return {
            label: 'Corrección',
            background: '#fde8e6',
            color: '#b31d15',
        };
    }

    if (commentType === 'submission') {
        return {
            label: 'Entrega',
            background: '#e6f4f8',
            color: '#0c6c84',
        };
    }

    return null;
};

const isImageUrl = (url) =>
    typeof url === 'string' && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url);

const getAttachmentItems = (comment) => {
    const a = comment.attachments || {};
    const items = [
        {
            key: 'file',
            label: 'Ver archivo',
            url: a.fileUrl || comment.image || null,
        },
        { key: 'audio', label: 'Ver audio', url: a.audioUrl || null },
        { key: 'video', label: 'Ver video', url: a.videoUrl || null },
    ].filter((x) => Boolean(x.url));
    return items;
};

const CommentCard = (props) => {
    const { comment, canEdit = false, onEdit } = props;
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [modalImageSrc, setModalImageSrc] = useState(null);
    const chip = getCommentChipConfig(comment.comment_type);
    const attachmentItems = getAttachmentItems(comment);
    const hasSingleImage =
        attachmentItems.length === 1 &&
        attachmentItems[0].key === 'file' &&
        isImageUrl(attachmentItems[0].url);

    const handleOpenImageModal = (src) => {
        setModalImageSrc(src);
        setIsImageModalOpen(true);
    };

    const handleCloseImageModal = () => {
        setIsImageModalOpen(false);
        setModalImageSrc(null);
    };

    return (
        <>
            <CommentBubble key={comment.id} isUser={comment.sender === 'user'}>
                <CommentHeader>
                    <AvatarImage>
                        <Image src={comment.photo} alt={comment.name} />
                    </AvatarImage>
                    <CommentAuthor>
                        <AuthorTopRow>
                            <AuthorName>
                                {comment.name}
                                {comment.author_is_teacher &&
                                    ' - Profesor tutor'}
                            </AuthorName>
                            {canEdit && (
                                <EditActionButton
                                    type="button"
                                    onClick={() => onEdit?.(comment)}
                                >
                                    Editar
                                </EditActionButton>
                            )}
                        </AuthorTopRow>
                        <HeaderMeta>
                            {chip && (
                                <TypeChip
                                    $background={chip.background}
                                    $color={chip.color}
                                >
                                    {chip.label}
                                </TypeChip>
                            )}
                            <CommentDate>{comment.date}</CommentDate>
                        </HeaderMeta>
                    </CommentAuthor>
                </CommentHeader>
                <CommentText>{comment.comment}</CommentText>
                {hasSingleImage && (
                    <AttachmentImage
                        src={attachmentItems[0].url}
                        alt="Adjunto"
                        onClick={() =>
                            handleOpenImageModal(attachmentItems[0].url)
                        }
                    />
                )}
                {(!hasSingleImage || attachmentItems.length > 1) &&
                    attachmentItems.length > 0 && (
                        <AttachmentButtonsRow>
                            {attachmentItems.map((att) => (
                                <AttachmentLink
                                    key={`${comment.id}-${att.key}`}
                                    href={att.url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <FontAwesomeIcon icon="fa-light fa-paperclip-vertical" />
                                    {att.label}
                                </AttachmentLink>
                            ))}
                        </AttachmentButtonsRow>
                    )}
            </CommentBubble>
            <ImageModal open={isImageModalOpen} onClose={handleCloseImageModal}>
                <ModalContent>
                    <CloseButton onClick={handleCloseImageModal}>
                        <Close />
                    </CloseButton>
                    {modalImageSrc && (
                        <ModalImage src={modalImageSrc} alt="Adjunto" />
                    )}
                </ModalContent>
            </ImageModal>
        </>
    );
};

export default CommentCard;

const CommentBubble = styled.div`
    display: flex;
    width: 85%;
    flex-direction: column;
    align-items: flex-start;
    align-self: ${(p) => (p.isUser ? 'flex-start' : 'flex-end')};
    margin-bottom: 8px;
    background: #f0f0f0;
    padding: 10px 14px;
    gap: 8px;
    border-radius: 18px;
`;

const Image = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    transition: transform 0.2s ease-in-out;
`;

const AvatarImage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background-color: #b31d15;
`;

const CommentHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
`;

const CommentAuthor = styled.div`
    display: flex;
    flex-direction: column;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: #333;
    min-width: 0;
    flex: 1;
`;

const AuthorTopRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    justify-content: space-between;
`;

const AuthorName = styled.span`
    color: #333;
    min-width: 0;
`;

const HeaderMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const EditActionButton = styled.button`
    border: none;
    background: transparent;
    color: #b31d15;
    font-size: 13px;
    font-weight: 600;
    padding: 0;
    cursor: pointer;
    line-height: 1.2;
    margin-left: auto;
    flex-shrink: 0;

    &:hover {
        text-decoration: underline;
    }
`;

const TypeChip = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    border-radius: 999px;
    background: ${(p) => p.$background};
    border: 1px solid ${(p) => p.$background};
    color: ${(p) => p.$color};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const CommentDate = styled.div`
    font-size: 11px;
    color: #a8a8a8;
`;

const CommentText = styled.div`
    color: #333;
    max-width: 100%;
    word-wrap: break-word;
    font-size: 14px;
    line-height: 1.4;
    white-space: pre-line;

    strong {
        font-size: 14px;
        font-weight: 600;
    }
`;

const AttachmentImage = styled.img`
    max-width: 200px;
    max-height: 200px;
    border-radius: 8px;
    object-fit: cover;
    cursor: pointer;
    margin-top: 4px;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.8;
    }
`;

const AttachmentButtonsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
`;

const AttachmentLink = styled.a`
    display: flex;
    align-items: center;
    gap: 4px;
    color: #b31d15;
    font-weight: 600;
    font-size: 13px;
    text-decoration: underline;

    &:hover {
        text-decoration: none;
    }
`;

const ImageModal = styled(Modal)`
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
`;

const ModalContent = styled.div`
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    outline: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ModalImage = styled.img`
    max-width: 100%;
    max-height: 90vh;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled(IconButton)`
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(255, 255, 255, 0.9);
    color: #000;
    z-index: 10000;

    &:hover {
        background: rgba(255, 255, 255, 1);
    }
`;
